import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import logo from "../../../public/assets/images/icon.png"; // Adjust the path as necessary

const Sidebar = ({ user, collapsed, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const navigationItems = [
    { 
      label: 'Dashboard', 
      path: user?.role === 'admin' ? '/admin-dashboard' : '/vendor-dashboard',
      icon: 'LayoutDashboard',
      roles: ['admin', 'vendor'],
      description: 'Overview and metrics'
    },
    { 
      label: 'Products', 
      path: '/product-management',
      icon: 'Package',
      roles: ['vendor','employee'],
      description: 'Manage product catalog'
    },
    { 
      label: 'Employees', 
      path: '/employee-management',
      icon: 'Users',
      roles: ['vendor'],
      description: 'Team management'
    },
    {
      label: 'Expenses',
      path: '/expenses',
      icon: 'CreditCard',
      roles: ['vendor'],
      description: 'Track expenses'
    },
    {
      label: 'Customers',
      path: '/customers',
      icon: 'Users',
      roles: ['vendor'],
      description: 'Manage customers'
    },
    { 
      label: 'Settings', 
      path: '/user-profile-settings',
      icon: 'Settings',
      roles: ['admin', 'vendor', 'employee'],
      description: 'Account preferences'
    },
  ];

  const filteredNavItems = navigationItems.filter(item => 
    item.roles.includes(user?.role)
  );

  const handleNavigation = (path) => {
    navigate(path);
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'admin': return 'Administrator';
      case 'vendor': return 'Vendor Manager';
      case 'employee': return 'Employee';
      default: return 'User';
    }
  };

  return (
    <aside 
      className={`fixed left-0 top-16 h-[calc(100vh-4rem)] bg-card border-r border-border z-900 sidebar-transition ${
        collapsed ? 'w-16' : 'w-60'
      } hidden lg:block`}
    >
      <div className="flex flex-col h-full">
        {/* Sidebar Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            {!collapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {user?.name || 'User Name'}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {getRoleDisplayName(user?.role)}
                </p>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={onToggle}
              className="micro-animation"
            >
              <Icon 
                name={collapsed ? "ChevronRight" : "ChevronLeft"} 
                size={16} 
              />
            </Button>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {filteredNavItems.map((item) => {
            const isActive = location.pathname === item.path;
            
            return (
              <div key={item.path} className="relative group">
                <Button
                  variant={isActive ? "default" : "ghost"}
                  size={collapsed ? "icon" : "default"}
                  fullWidth={!collapsed}
                  onClick={() => handleNavigation(item.path)}
                  className={`micro-animation ${
                    collapsed ? 'justify-center' : 'justify-start'
                  } ${isActive ? 'shadow-sm' : ''}`}
                >
                  <Icon name={item.icon} size={20} />
                  {!collapsed && (
                    <span className="ml-3 text-sm font-medium">
                      {item.label}
                    </span>
                  )}
                </Button>

                {/* Tooltip for collapsed state */}
                {collapsed && (
                  <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-popover border border-border rounded-md shadow-modal opacity-0 group-hover:opacity-100 transition-opacity duration-150 pointer-events-none z-1200 whitespace-nowrap">
                    <div className="text-xs font-medium text-popover-foreground">
                      {item.label}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {item.description}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-border">
          {!collapsed ? (
            <div className="space-y-2">
              <div className="flex items-center space-x-3 p-2 rounded-md bg-muted/50">
                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                  <img src={logo} alt="logo" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-foreground">VendorHub</p>
                  <p className="text-xs text-muted-foreground">v2.1.0</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                <Icon name="Building2" size={16} color="white" />
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;